import styles from './add-contract-dialog.module.scss';
import { useContext, useEffect, useState } from 'react';
import { useGetRequestNewHook, useMakeRequestHook } from '../../requestUtils';
import { ContactListContext, GetCurrentCompany } from '../../customer-context/customer-context';
import { Col, Row } from 'reactstrap';
import { addMonths } from 'date-fns';
import {
  Autocomplete, CheckboxLabel, CompanySearch, ContractStatusEnum, ContractTypeEnum, CurrencyEnum, DatePicker, Dialog, DialogActions, DialogContent, DialogTitle, getISODate,
  GrayOutlinedButton, IUserAccount, MenuItem, PrimaryButton, Select, TextField, useSingleContractDispatch, VismaClientEnum
} from '@frontend-workspace/react-shared-lib';
import { IntervalEnum } from '../../_enums/interval';
import { useTranslation } from 'react-i18next';

/* eslint-disable-next-line */
export interface AddContractDialogProps {
  open: boolean,
  onClose: () => void
}

export function AddContractDialog(props: AddContractDialogProps) {
  const { t } = useTranslation();
  const currentCompany = GetCurrentCompany();
  const makeRequest = useMakeRequestHook();
  const contactList = useContext(ContactListContext);
  const [selectedCompanyNumber, setSelectedCompanyId] = useState<string | undefined>(currentCompany?.number);
  const companyNumber = currentCompany ? currentCompany.number : "";
  const responsibleContact = contactList.find(c => c.number === selectedCompanyNumber);
  const { data: customerEmployees, isLoading: isLoadingAccounts }: { data: IUserAccount[], isLoading: boolean } = useGetRequestNewHook(`companies/${responsibleContact?.number}/UserAccounts/forGroup/${true}/onlyactive/true`, [], [responsibleContact]);
  const { data: groupsAndSeller, isLoading } = useGetRequestNewHook(`companies/${companyNumber}/licenseGroupsAndSeller/`, { sellerName: "", groups: [] }, [companyNumber]);
  const dispatch = useSingleContractDispatch();

  const [interval, setInterval] = useState(IntervalEnum.ONE_YEAR);
  const [contractStart, setContractStart] = useState<Date>(new Date());
  const [currency, setCurrency] = useState<CurrencyEnum>(CurrencyEnum.NOK);
  const [contractType, setContractType] = useState<ContractTypeEnum>(ContractTypeEnum.FOCUS)
  const [isAcademic, setIsSchoolVersion] = useState<boolean>(false);
  const [isSupportContract, setIsSupportContract] = useState<boolean>(false);
  //const [contractResponsibleId, setContractResponsibleId] = useState<number | undefined>(1);
  const [contractResponsibleEmail, setContractResponsibleEmail] = useState<string | undefined>(undefined);
  const companyWithoutAccounts = !isLoadingAccounts && customerEmployees.length === 0;

  const calcNextInvoice = (date: Date, newInterval?: number) => addMonths(date, newInterval || interval);
  const [contractRenewal, setContractRenewal] = useState<Date>(calcNextInvoice(contractStart));

  const handleCompanyChange = (companyNumber: string) => setSelectedCompanyId(companyNumber);
  const handleCurrency = (event: any) => setCurrency(event.target.value);
  const handleVismaClientSelect = (event: any) => setContractType(event.target.value);
  const handleContractResponsibleSelected = (email: string) => setContractResponsibleEmail(email);
  const toggleIsSchoolVersion = () => setIsSchoolVersion(!isAcademic);

  const handleIntervalChange = (event: any) => {
    setInterval(event.target.value);
    setContractRenewal(calcNextInvoice(contractStart, event.target.value));
  };

  const handleSetIsSupportContract = (value: boolean) => {
    setIsSupportContract(value);
    setIsSchoolVersion(false);
  };

  const handleNextInvoiceChange = (date: Date | null) => {
    if (date) setContractRenewal(date);
  };

  const handleSetContractStart = (date: Date | null) => {
    if (date) {
      setContractStart(date);
      setContractRenewal(calcNextInvoice(date));
    }
  };

  useEffect(() => {
    makeRequest(`companies/${companyNumber}/admin`).then((result: any) => setContractResponsibleEmail(result.email));
  }, []);

  const handleNewContract = () => {
    if (contractResponsibleEmail) {
      let vismaClient = VismaClientEnum.FOCUS;
      if (contractType === ContractTypeEnum.BYGGDATA) vismaClient = VismaClientEnum.BYGGDATA;
      if (contractType === ContractTypeEnum.SLETTEN) vismaClient = VismaClientEnum.SLETTEN;
      dispatch({
        type: "INITIALIZE_NEW_CONTRACT",
        companyName: currentCompany?.name,
        contractInfo: {
          contractResponsibleEmail,
          status: ContractStatusEnum.ACTIVE,
          activationDate: getISODate(contractStart),
          sellerEmail: undefined,
          note: "",
          invoiceInterval: interval,
          nextInvoicingDate: getISODate(contractRenewal),
          currency: currency
        },
        invoiceInfo: {
          invoicedCompanyNumber: companyNumber,
          invoiceResponsibleName: "",
          note: "",
          reference: "",
          vismaClient: vismaClient,
        },
        isAcademic: isAcademic,
        contractType: contractType,
        isSupport: isSupportContract,
        groups: groupsAndSeller.groups,
        sellerName: groupsAndSeller.sellerName
      });
      makeRequest(`contracts/actions/getSellableProducts/academic/${isAcademic}/contractType/${contractType}/interval/${interval}/currency/${currency}`)
        .then(result => dispatch({ type: "SELLABLE_PRODUCTS_RECEIVED", sellableProducts: result }));
    }
  };

  return (
    <Dialog
      open={props.open}
      onClose={props.onClose}
      maxWidth="sm"
      fullWidth>
      <DialogTitle className='mb-4'>
        {t('addContract.newContract')}
      </DialogTitle>
      {companyWithoutAccounts ?
        <DialogContent>
          {t('addContract.thereAreNoAccountsForThisCustomer')}
        </DialogContent> :
        <DialogContent>
          <Row>
            <Col md={6}>
              <div className='mb-3'>
                <Select
                  displayEmpty
                  value={interval}
                  onChange={handleIntervalChange}
                  fullWidth
                  label={t('standard.invoiceInterval')}
                  name='interval'>
                  <MenuItem value={IntervalEnum.HALF_YEAR}>6 {t('addContract.months')}</MenuItem>
                  <MenuItem value={IntervalEnum.ONE_YEAR}>1 {t('addContract.year')}</MenuItem>
                  <MenuItem value={IntervalEnum.THREE_YEARS}>3 {t('addContract.year')}</MenuItem>
                </Select>
              </div>
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              <div className='mb-3'>
                <DatePicker
                  inputFormat="dd.MM.yyyy"
                  label={t('addContract.contractStart')}
                  value={contractStart}
                  onChange={handleSetContractStart}
                />
              </div>
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              <div className='mb-3'>
                <DatePicker
                  inputFormat="dd.MM.yyyy"
                  label={t('addContract.contractRenewal')}
                  value={contractRenewal}
                  onChange={handleNextInvoiceChange}
                />
              </div>
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              <div className='mb-3'>
                <Select
                  displayEmpty
                  value={currency}
                  onChange={handleCurrency}
                  fullWidth
                  label={t('standard.currency')}
                  name='currency'>
                  <MenuItem value={'NOK'}>NOK</MenuItem>
                  <MenuItem value={'SEK'}>SEK</MenuItem>
                  <MenuItem value={'EUR'}>EUR</MenuItem>
                  <MenuItem value={'DKK'}>DKK</MenuItem>
                  <MenuItem value={'USD'}>USD</MenuItem>
                </Select>
              </div>
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              <div className='mb-3'>
                <Select
                  displayEmpty
                  fullWidth
                  label={t('addContract.selectContractType')}
                  name='contractType'
                  value={contractType}
                  onChange={handleVismaClientSelect}>
                  <MenuItem value={ContractTypeEnum.FOCUS}>Focus</MenuItem>
                  <MenuItem value={ContractTypeEnum.SUPPORT} onClick={handleSetIsSupportContract}>{t('standard.userSupport')}</MenuItem>
                  {/* <MenuItem value={ContractTypeEnum.SLETTEN}>Sletten</MenuItem>
                  <MenuItem value={ContractTypeEnum.BYGGDATA}>Byggdata</MenuItem> */}
                </Select>
              </div>
              <div className='mb-3'>
                <CheckboxLabel
                  label={t('standard.academic')}
                  checked={isAcademic}
                  disabled={contractType === ContractTypeEnum.SUPPORT}
                  onChange={toggleIsSchoolVersion}
                />
              </div>
            </Col>
          </Row>
          <div className='mb-3'>
            <label>{t('standard.customerNumber')}</label>
            <CompanySearch
              selectedCompanyNumber={selectedCompanyNumber}
              companyList={contactList}
              onChange={handleCompanyChange}
            />
          </div>
          {customerEmployees.length > 0 ?
            <div className='mb-3'>
              <Autocomplete
                value={customerEmployees.find(e => e.email === contractResponsibleEmail)}
                getOptionLabel={(option: any) => `${option.firstName} ${option.lastName}` || ""}
                options={customerEmployees}
                renderInput={(params: any) => {
                  return <TextField
                    {...params}
                    fullWidth
                    label={t('standard.contractManager')}
                    sx={{ minWidth: '320px' }}
                  />
                }}
                renderOption={(props: any, option: any) => <li {...props} key={option.email}>
                  {option.firstName} {option.lastName}  ({option.email})
                </li>
                }
                onChange={(event: any, userAccount: any) => {
                  handleContractResponsibleSelected(userAccount?.email)
                }}
              />
            </div> : <div className='mb-3'>{t('addContract.retrievesEmployees')}</div>
          }
        </DialogContent>
      }
      <DialogActions>
        {!companyWithoutAccounts &&
          <PrimaryButton
            variant="contained"
            size='large'
            disabled={isLoading}
            onClick={handleNewContract}
            className='minButtonWidth'>
            {isLoading ? t('standard.retrievingData') : "OK"}
          </PrimaryButton>
        }
        <GrayOutlinedButton
          variant="outlined"
          size='large'
          onClick={props.onClose}
          className='minButtonWidth'>
          {t('standard.cancel')}
        </GrayOutlinedButton>
      </DialogActions>
    </Dialog >
  );
}

export default AddContractDialog;